import { useNavigate, useLocation } from "react-router-dom";
import {
    Sparkles,
    Scissors,
    Baby,
    ChefHat,
    Wand2,
    ShoppingBag,
    Car,
    Plane,
    Wrench,
    Shirt,
    Truck,
} from "lucide-react";

/**
 * Persistent Services Menu Bar.
 *
 * A horizontally-scrollable strip showing every SMART service category.
 * Pinned sticky under the Header so visitors can jump to any service from
 * any page without having to go back to the home page first.
 *
 * Mounted globally from App.js. Hidden on /auth flows and dashboards where
 * the user is already deep inside a workflow.
 */
const CATEGORIES = [
    { id: "Cleaning", label: "Cleaning", Icon: Sparkles },
    { id: "Beauty", label: "Beauty & Glam", Icon: Scissors },
    { id: "Childcare", label: "Childcare", Icon: Baby },
    { id: "Chef", label: "Private Chef", Icon: ChefHat },
    { id: "Decor", label: "Decor", Icon: Wand2 },
    { id: "Grocery", label: "Grocery", Icon: ShoppingBag },
    { id: "Auto", label: "Car Wash", Icon: Car },
    { id: "Transport", label: "Transport", Icon: Plane },
    { id: "Handyman", label: "Handyman", Icon: Wrench },
    { id: "Laundry", label: "Laundry", Icon: Shirt },
    { id: "Moving", label: "Moving", Icon: Truck, badge: "NEW" },
];

// Routes where we hide the services bar (auth flows + admin console).
// Anywhere else it shows so visitors can jump to any service in one tap.
const HIDDEN_ROUTES = [
    "/auth",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/admin",
    "/payment-success",
    "/payment-cancelled",
];

export default function ServicesMenuBar() {
    const navigate = useNavigate();
    const location = useLocation();

    const path = location.pathname || "/";
    if (HIDDEN_ROUTES.some((r) => path === r || path.startsWith(r + "/"))) {
        return null;
    }

    return (
        <nav
            className="services-menu-bar"
            data-testid="services-menu-bar"
            aria-label="Service categories quick navigation"
        >
            <div className="services-menu-bar-inner">
                {CATEGORIES.map(({ id, label, Icon, badge }) => {
                    const active = location.search.includes(`category=${id}`);
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => navigate(`/services?category=${id}`)}
                            data-testid={`menu-bar-cat-${id.toLowerCase()}`}
                            className={`services-menu-item ${active ? "is-active" : ""}`}
                        >
                            <span className="services-menu-icon" aria-hidden="true">
                                <Icon size={16} strokeWidth={1.5} />
                            </span>
                            <span className="services-menu-label">{label}</span>
                            {badge && (
                                <span className="services-menu-badge" data-testid={`menu-bar-badge-${id.toLowerCase()}`}>
                                    {badge}
                                </span>
                            )}
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={() => navigate("/services")}
                    data-testid="menu-bar-all"
                    className="services-menu-item services-menu-all"
                >
                    <span className="services-menu-label">All services →</span>
                </button>
            </div>
        </nav>
    );
}
